import { AddRounded } from "@mui/icons-material";
import { DateTime } from "luxon";
import { type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditTimePopover } from "../../../../../components/EditTimePopover/EditTimePopover";
import MetaChip from "../../../../../components/UIToolkit/Badges/MetaChip";
import { ChipField } from "../../../../../components/UIToolkit/Inputs/ChipField";
import SystemButton from "../../../../../components/UIToolkit/SystemButton/SystemButton";
import { CONTROL_HEIGHT, FONT_SIZE } from "../../../../../theme/tokens";
import { parseTimeString } from "../../../../../utils/dateUtils";
import type { ShiftHourWindow } from "../../../../../utils/models";
import { DEFAULT_WINDOW, WINDOW_TIME_FORMAT } from "./autoApproveConsts";
import { windowLabelOf } from "./ruleSummary";

const F = "department.config.staffApp.autoApprove.fields";

type Props = { windows: ShiftHourWindow[]; onChange: (windows: ShiftHourWindow[]) => void; testIdPrefix: string };

/** A rule's "HH:MM" window time as today's DateTime, for the time popover. */
const toDateTime = (time: string): DateTime => {
	const { hour, minute } = parseTimeString(time);
	return DateTime.now().set({ hour, minute, second: 0, millisecond: 0 });
};

/** The start-time windows a rule applies to, as chips in one field: each opens the time popover, and none means any hours. */
export const AutoApproveRuleWindows: FC<Props> = ({ windows, onChange, testIdPrefix }) => {
	const { t, i18n } = useTranslation();
	const [editing, setEditing] = useState<{ window: ShiftHourWindow; anchorEl: HTMLElement }>();
	const activeWindow = editing && windows.includes(editing.window) ? editing.window : undefined;

	useEffect(() => {
		if (editing && !activeWindow) setEditing(undefined);
	}, [editing, activeWindow]);

	return (
		<>
			<ChipField
				empty={windows.length === 0}
				placeholder={t(`${F}.hours.any`)}
				ariaLabel={t(`${F}.hours.label`)}
				dataTestId={`${testIdPrefix}-windows`}
				action={
					<SystemButton
						buttonVariant="borderless"
						size="small"
						noFullWidthMobile
						leadingIcon={<AddRounded fontSize="small" />}
						onClick={() => onChange([...windows, { ...DEFAULT_WINDOW }])}
						data-testid={`${testIdPrefix}-add-window`}
						sx={{ minHeight: CONTROL_HEIGHT.COMPACT, height: CONTROL_HEIGHT.COMPACT, px: 1, fontSize: FONT_SIZE.SECONDARY, color: "primary.dark" }}
					>
						{t("department.config.staffApp.autoApprove.addWindow")}
					</SystemButton>
				}
			>
				{windows.map((window, index) => (
					<MetaChip
						// biome-ignore lint/suspicious/noArrayIndexKey: windows have no id, and two can be identical.
						key={`${window.start}-${window.end}-${index}`}
						size="small"
						tone="primary"
						label={windowLabelOf(window, i18n.language)}
						ariaLabel={t(`${F}.hours.edit`, { window: windowLabelOf(window, i18n.language) })}
						onClick={(event) => setEditing({ window, anchorEl: event.currentTarget })}
						onRemove={() => {
							setEditing(undefined);
							onChange(windows.filter((_, other) => other !== index));
						}}
						removeLabel={t("department.config.staffApp.autoApprove.removeWindow")}
						dataTestId={`${testIdPrefix}-window-start-${index}`}
						removeDataTestId={`${testIdPrefix}-window-remove-${index}`}
					/>
				))}
			</ChipField>
			{editing && activeWindow && (
				<EditTimePopover
					startTime={toDateTime(activeWindow.start)}
					endTime={toDateTime(activeWindow.end)}
					anchorEl={editing.anchorEl}
					onTimeChange={({ newStartTime, newEndTime }: { newStartTime: DateTime; newEndTime: DateTime }) => {
						onChange(
							windows.map((window) =>
								window === activeWindow ? { start: newStartTime.toFormat(WINDOW_TIME_FORMAT), end: newEndTime.toFormat(WINDOW_TIME_FORMAT) } : window,
							),
						);
						setEditing(undefined);
					}}
					onClose={() => setEditing(undefined)}
					hidePatialShiftLengths
				/>
			)}
		</>
	);
};
